import { accountKindOf } from '@sharedlib/common/utils/account-kind';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { accountLandingUrl } from '@frontendlib/common/utils/cross-app-nav';
import { AuthService } from '@frontendlib/common/services/auth.service';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { DeviceSessionsService } from '@frontendlib/common/services/device-sessions.service';
import { enteredSessionHorizon } from '@frontendlib/common/utils/entered-session-horizon';
import { IGNORE_UNAUTHORIZED_REDIRECT } from '@frontendlib/common/utils/auth';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { environment } from '@environment';
import { type NotificationOperation } from '@frontendlib/notifications';
import { AlertService } from '@modules/alert/services/alert.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { ModalOpenRegistry } from '@modules/modal/services/modal-open-registry.service';
import { SettingsNavigationCancellation } from '@modules/settings-window/account/settings-subpage.guard';
import { type NotificationActionResult } from '@sharedlib/notification-contract';
import { firstValueFrom, fromEvent, Subject, takeUntil, timeout } from 'rxjs';
import {
  notificationDate,
  notificationKeys,
  notificationRecord,
  notificationUuid,
  parseNotificationAction,
} from './notification-feed.validation';
import { notificationSettingsOpen } from './hosted-notification-view.service';

import { NotificationPanelDraftsService } from './notification-panel-drafts.service';
import { NotificationSwitchingService } from './notification-switching.service';

export const NOTIFICATION_PENDING_CLICK_KEY = 'stitch.notifications.pending-click';
export const NOTIFICATION_PENDING_CLICK_TTL_MS = 60_000;

interface PendingClick {
  accountId: string;
  deliveryId: string;
  expiresAt: string;
}

export function parsePendingNotificationClick(value: unknown, accountId: string, now: number): PendingClick | null {
  if (
    !notificationRecord(value) ||
    !notificationKeys(value, ['accountId', 'deliveryId', 'expiresAt']) ||
    value['accountId'] !== accountId ||
    !notificationUuid(value['accountId']) ||
    !notificationUuid(value['deliveryId']) ||
    !notificationDate(value['expiresAt']) ||
    Date.parse(value['expiresAt']) <= now ||
    Date.parse(value['expiresAt']) > now + NOTIFICATION_PENDING_CLICK_TTL_MS
  )
    return null;
  return { accountId: value['accountId'], deliveryId: value['deliveryId'], expiresAt: value['expiresAt'] };
}

/** Notification clicks resolve authority again; neither native payload nor storage carries a destination. */
@Injectable({ providedIn: 'root' })
export class HostedNotificationActionsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly currentUser = inject(CurrentUserStore);
  private readonly devices = inject(DeviceSessionsService);
  private readonly router = inject(Router);
  private readonly forms = inject(FormChangesModalsService);
  private readonly modals = inject(ModalOpenRegistry);
  private readonly alerts = inject(AlertService);
  private readonly toaster = inject(ToasterService);
  private readonly document = inject(DOCUMENT);
  private readonly switching = inject(NotificationSwitchingService);
  private readonly panels = inject(NotificationPanelDraftsService);
  private busy = false;

  public async activate(accountId: string, deliveryId: string, operation: NotificationOperation): Promise<void> {
    if (this.busy || operation.signal.aborted || !notificationUuid(accountId) || !notificationUuid(deliveryId)) return;
    this.busy = true;
    let releaseSwitch: (() => void) | undefined;
    const activeAccount = this.currentUser.accountId();
    const current = (): boolean => !operation.signal.aborted && this.currentUser.accountId() === activeAccount;
    try {
      const result = await this.resolve(accountId, deliveryId, operation.signal);
      if (!current() || !result || result.status === 'denied' || result.status === 'unavailable')
        return this.unavailable(current);
      if (
        (accountId === activeAccount && result.status !== 'allowed') ||
        (accountId !== activeAccount && result.status !== 'switch-required')
      )
        return this.unavailable(current);
      if (result.status === 'switch-required' && !(await this.confirmSwitch(operation.signal))) return;
      if (!current() || !(await this.prepareToLeave(operation.signal)) || !current()) return;
      // A user may keep a draft dialog open for minutes: never reuse the answer from before it.
      const fresh = await this.resolve(accountId, deliveryId, operation.signal);
      if (!current() || !fresh) return;
      if (
        this.modals.hasOpenModal() ||
        this.forms.hasUnsavedChanges() ||
        this.panels.hasUnsavedChanges() ||
        notificationSettingsOpen(this.router.routerState.root)
      )
        return;
      if (accountId === activeAccount && fresh.status === 'allowed') {
        const { spaceId, channelId, laneId, seq } = fresh.target;
        // 010: a reply's seq counts inside its thread, so it opens the thread at the reply, as a Mentions row does.
        await this.router.navigate(['/spaces', spaceId, 'chat', channelId], {
          queryParams: laneId === null ? { at: seq } : { lane: laneId, reply: seq },
        });
      } else if (accountId !== activeAccount && fresh.status === 'switch-required') {
        if (this.modals.hasOpenModal() || !current()) return;
        // Once sent, the cookie switch must finish even if the notification is cancelled.
        // The boundary callback retains only intent admitted before our own invalidation.
        releaseSwitch = this.switching.block();
        let admittedBeforeSwitchInvalidation = false;
        const details = await firstValueFrom(
          this.devices.switchTo(accountId, this.context(), () => {
            admittedBeforeSwitchInvalidation = current();
          }),
        );
        this.auth.setAuthPayload({
          accountId: details.id,
          exp: enteredSessionHorizon(details.id),
          recoveryEmailVerified: details.recoveryEmailVerified,
          accountKind: accountKindOf(details),
        });
        try {
          if (admittedBeforeSwitchInvalidation && details.id === accountId) {
            this.pending().set({
              accountId,
              deliveryId,
              expiresAt: new Date(Date.now() + NOTIFICATION_PENDING_CLICK_TTL_MS).toISOString(),
            });
          }
        } catch {
          // Cookie already switched: reload to the entered account, but do not navigate from stale data.
        }
        this.document.defaultView?.location.assign(accountLandingUrl(details));
      } else {
        this.unavailable(current);
      }
    } catch {
      this.unavailable(current);
    } finally {
      releaseSwitch?.();
      this.busy = false;
    }
  }

  /** Called only after native negotiation and admitted-feed startup in the entered document. */
  public async resume(signal: AbortSignal): Promise<void> {
    const accountId = this.currentUser.accountId();
    if (!accountId || signal.aborted || this.busy) return;
    let pending: PendingClick | null;
    try {
      const slot = this.pending();
      pending = parsePendingNotificationClick(slot.getSync(), accountId, Date.now());
      slot.set(undefined);
    } catch {
      return;
    }
    if (!pending) return;
    await this.activate(accountId, pending.deliveryId, { signal, identityGeneration: 'post-switch-click' });
  }

  private pending(): LocalStorageItem<unknown> {
    return new LocalStorageItem<unknown>(NOTIFICATION_PENDING_CLICK_KEY, { storage: 'session' });
  }

  private async resolve(
    accountId: string,
    deliveryId: string,
    signal: AbortSignal,
  ): Promise<NotificationActionResult | null> {
    if (signal.aborted) return null;
    const result = await firstValueFrom(
      this.http
        .post<unknown>(
          environment.baseUrl + '/api/account/sessions/notifications/action',
          { version: 1, accountId, deliveryId },
          { withCredentials: true, context: this.context() },
        )
        .pipe(takeUntil(fromEvent(signal, 'abort')), timeout(3_000)),
    );
    return signal.aborted ? null : parseNotificationAction(result);
  }

  private context(): HttpContext {
    return new HttpContext().set(IGNORE_UNAUTHORIZED_REDIRECT, true);
  }

  private async prepareToLeave(signal: AbortSignal): Promise<boolean> {
    if (this.modals.hasOpenModal()) {
      this.toaster.show({ text: 'Finish or close your open dialog before opening this notification.' });
      return false;
    }
    if (!(await firstValueFrom(this.forms.confirmUnsavedChanges(undefined, false, signal))) || signal.aborted)
      return false;
    if (!(await this.panels.confirm(signal)) || signal.aborted) return false;
    if (notificationSettingsOpen(this.router.routerState.root)) {
      // Closing through the real named outlet runs Settings' scoped CanDeactivate guard.
      const closed = await this.router.navigate([{ outlets: { settings: null } }], {
        relativeTo: this.router.routerState.root.firstChild,
        info: new SettingsNavigationCancellation(signal),
      });
      if (!closed || signal.aborted) return false;
    }
    return !this.modals.hasOpenModal() && !signal.aborted;
  }

  private confirmSwitch(signal: AbortSignal): Promise<boolean> {
    if (signal.aborted) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const closed = new Subject<void>();
      let finished = false;
      const finish = (answer: boolean): void => {
        if (finished) return;
        finished = true;
        signal.removeEventListener('abort', abort);
        closed.next();
        closed.complete();
        resolve(answer);
      };
      const abort = (): void => finish(false);
      signal.addEventListener('abort', abort, { once: true });
      this.alerts.show({
        title: 'Switch account?',
        body: 'This message belongs to another signed-in account. Switch to open it?',
        appearance: 'prompt',
        showUntil: closed,
        onClose: () => finish(false),
        rightButtons: [
          { title: 'Cancel', appearance: 'secondary', close: true, click: () => finish(false) },
          { title: 'Switch account', appearance: 'primary', close: true, click: () => finish(true) },
        ],
      });
    });
  }

  private unavailable(current: () => boolean): void {
    if (current())
      this.toaster.show({ text: 'This notification is no longer available. Please open the chat from Stitch.' });
  }
}
